import React, { useState, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  CreditCard, Power, X, Download, Send, RefreshCw,
  Home, Layers, TrendingUp, ChevronDown, ChevronUp, Settings
} from 'react-feather';
import { FaEthereum } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../../../assets/logo.png';

// --- STYLED COMPONENTS (CSS-in-JS) ---
const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 2000;
  opacity: ${({ show }) => (show ? 1 : 0)};
  visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  backdrop-filter: blur(4px);

  @media (min-width: 1024px) {
    display: none;
  }
`;

const MenuContainer = styled.div`
  position: fixed; top: 0; right: 0; width: 82%; max-width: 340px; height: 100%;
  background-color: ${p => p.theme.colors.background.paper};
  box-shadow: -4px 0 24px rgba(0,0,0,0.25);
  transform: ${({ show }) => (show ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex; flex-direction: column; z-index: 2001;

  @media (min-width: 1024px) {
    display: none;
  }
`;

const MenuHeader = styled.header`
  display: flex; justify-content: space-between; align-items: center;
  padding: 20px 22px; padding-top: calc(env(safe-area-inset-top, 20px) + 12px);
  border-bottom: 1px solid ${p => p.theme.colors.border.main};
  flex-shrink: 0;
  background: ${p => p.theme.colors.background.paper};
`;

const MenuLogo = styled.img`
  width: 130px; height: auto; object-fit: contain;
  filter: ${p => p.theme.isDark ? 'brightness(0) invert(1)' : 'brightness(0)'};
`;

const CloseButton = styled.button`
  background: ${p => p.theme.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  border: 1px solid ${p => p.theme.colors.border.main};
  border-radius: 10px;
  cursor: pointer;
  padding: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  &:hover {
    background: ${p => p.theme.colors.background.selected};
  }
`;

const MenuScrollView = styled.nav`
  flex-grow: 1; overflow-y: auto; -webkit-overflow-scrolling: touch;
`;

const MenuItem = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 15px 22px;
  cursor: pointer;
  border-bottom: 1px solid ${p => p.theme.colors.border.main};
  opacity: ${({ disabled }) => (disabled ? 0.45 : 1)};
  transition: background 0.15s;
  &:active { background-color: ${p => p.theme.colors.background.selected}; }
`;

const MenuItemLeft = styled.div`
  display: flex; align-items: center; gap: 16px;
`;

const MenuItemText = styled.span`
  font-size: 15.5px;
  font-weight: 600;
  color: ${p => p.theme.colors.text.primary};
  letter-spacing: -0.1px;
`;

const SubMenu = styled.div`
  background-color: ${p => p.theme.colors.background.main};
  overflow: hidden;
  max-height: ${({ expanded }) => (expanded ? '500px' : '0')};
  transition: max-height 0.3s ease-in-out;
  border-bottom: ${({ expanded, theme }) => (expanded ? `1px solid ${theme.colors.border.main}` : 'none')};
`;

const SubMenuItem = styled.div`
  padding: 13px 22px 13px 60px;
  font-size: 14px;
  font-weight: 500;
  color: ${p => p.theme.colors.text.secondary};
  cursor: pointer;
  border-bottom: 1px solid ${p => p.theme.colors.border.main};
  transition: all 0.15s;
  &:hover { background-color: ${p => p.theme.colors.background.selected}; color: ${p => p.theme.colors.text.primary}; }
  &:last-child { border-bottom: none; }
`;

const BadgePill = styled.span`
  font-size: 9px;
  font-weight: 700;
  background: rgba(245,158,11,0.15);
  color: #f59e0b;
  border-radius: 6px;
  padding: 3px 7px;
  margin-left: 8px;
  letter-spacing: 0.5px;
`;

const MenuFooter = styled.footer`
  border-top: 1px solid ${p => p.theme.colors.border.main};
  padding: 16px 22px;
  padding-bottom: calc(env(safe-area-inset-bottom, 20px) + 90px);
  flex-shrink: 0;
  background: ${p => p.theme.colors.background.paper};
`;

const LogoutButton = styled.button`
  display: flex; align-items: center; justify-content: flex-start;
  background: ${p => p.theme.colors.background.selected};
  border: 1px solid ${p => p.theme.colors.border.main};
  border-radius: 12px; cursor: pointer; padding: 14px; width: 100%;
  transition: all 0.2s;
  gap: 10px;
  &:hover {
    background: ${p => p.theme.isDark ? 'rgba(239, 68, 68, 0.15)' : '#fee2e2'};
    border-color: ${p => p.theme.isDark ? 'rgba(239, 68, 68, 0.4)' : '#fca5a5'};
  }
`;

const LogoutButtonText = styled.span`
  font-size: 15px;
  color: ${p => p.theme.colors.text.primary};
  font-weight: 700;
`;

const IconWrapper = styled.div`
  width: 38px; height: 38px;
  border-radius: 10px;
  background: ${p => p.$active
    ? (p.theme.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)')
    : p.theme.colors.background.selected};
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s;
`;


// --- COMPONENTE DE REACT ---

const MenuModal = ({ showMenu, setShowMenu, onLogout }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isBusiness = location.pathname.includes('/business');
  const basePath = isBusiness ? '/business' : '/cross_border';

  const [expandedItem, setExpandedItem] = useState(null);

  const menuItems = [
    { id: 'home', icon: Home, text: 'Dashboard', link: `${basePath}/home` },
    {
      id: 'accounts', icon: Layers, text: 'My Accounts', link: null,
      subItems: [
        { text: 'Account Details', navId: 'accounts', state: { myAccountTab: 'details' } },
        { text: 'Transaction History', navId: 'accounts', state: { myAccountTab: 'txHistory' } },
        { text: 'Statements', navId: 'accounts', state: { myAccountTab: 'statements' } },
      ]
    },
    {
      id: 'transfer', icon: Send, text: 'Payments', link: null,
      subItems: [
        { text: 'Domestic', navId: 'transfer/domestic' },
        { text: 'International', navId: 'transfer/international' },
        { text: 'Mass Payments', navId: 'transfer/mass' },
      ]
    },
    { id: 'fx', icon: RefreshCw, text: 'FX Exchange', link: null, isComingSoon: true },
    { id: 'settings', icon: Settings, text: 'Settings', link: `${basePath}/settings` },
    { id: 'crypto', icon: FaEthereum, text: 'Crypto OTC', link: null, isComingSoon: true },
    { id: 'investments', icon: TrendingUp, text: 'Investments', link: null, isComingSoon: true },
    { id: 'cards', icon: CreditCard, text: 'Cards', link: null, isComingSoon: true },
  ];

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleMenuItemClick = (item) => {
    if (item.isComingSoon) return;

    if (item.subItems) {
      setExpandedItem(expandedItem === item.id ? null : item.id);
    } else if (item.link) {
      navigate(item.link);
      setShowMenu(false);
    }
  };

  const handleSubItemClick = (subItem) => {
    navigate(`${basePath}/${subItem.navId}`, { state: subItem.state || {} });
    setShowMenu(false);
  };

  const iconColor = theme.colors.text.secondary;
  const iconActiveColor = theme.colors.text.primary;

  return (
    <ModalOverlay show={showMenu} onClick={() => setShowMenu(false)}>
      <MenuContainer show={showMenu} onClick={(e) => e.stopPropagation()}>
        <MenuHeader>
          <MenuLogo src={logo} alt="NYBANQ Logo" />
          <CloseButton onClick={() => setShowMenu(false)}>
            <X size={22} color={theme.colors.text.secondary} />
          </CloseButton>
        </MenuHeader>
        <MenuScrollView>
          {deferredPrompt && (
            <MenuItem onClick={() => deferredPrompt.prompt()}>
              <MenuItemLeft>
                <IconWrapper>
                  <Download size={18} color={iconColor} />
                </IconWrapper>
                <MenuItemText>Install App</MenuItemText>
              </MenuItemLeft>
            </MenuItem>
          )}

          {menuItems.map((item, index) => {
            const isExpanded = expandedItem === item.id;
            const Icon = item.icon;
            const isCurrentRoute = item.link && location.pathname.startsWith(item.link);
            return (
              <React.Fragment key={index}>
                <MenuItem
                  onClick={() => handleMenuItemClick(item)}
                  disabled={item.isComingSoon}
                >
                  <MenuItemLeft>
                    <IconWrapper $active={isCurrentRoute}>
                      <Icon
                        size={18}
                        color={item.isComingSoon ? theme.colors.text.secondary : (isCurrentRoute ? iconActiveColor : iconColor)}
                      />
                    </IconWrapper>
                    <MenuItemText style={{ color: item.isComingSoon ? theme.colors.text.secondary : theme.colors.text.primary }}>
                      {item.text}
                    </MenuItemText>
                    {item.isComingSoon && <BadgePill>SOON</BadgePill>}
                  </MenuItemLeft>
                  {item.subItems && (
                    isExpanded
                      ? <ChevronUp size={16} color={theme.colors.text.secondary} />
                      : <ChevronDown size={16} color={theme.colors.text.secondary} />
                  )}
                </MenuItem>

                {item.subItems && (
                  <SubMenu expanded={isExpanded}>
                    {item.subItems.map((subItem, idx) => (
                      <SubMenuItem key={idx} onClick={() => handleSubItemClick(subItem)}>
                        {subItem.text}
                      </SubMenuItem>
                    ))}
                  </SubMenu>
                )}
              </React.Fragment>
            );
          })}
        </MenuScrollView>
        <MenuFooter>
          <LogoutButton onClick={onLogout}>
            <Power size={16} color={theme.isDark ? '#f87171' : '#dc2626'} />
            <LogoutButtonText>Log Out</LogoutButtonText>
          </LogoutButton>
        </MenuFooter>
      </MenuContainer>
    </ModalOverlay>
  );
};

export default MenuModal;